/**
 * Native-aware Firebase Auth wrapper.
 *
 * Why this exists: The Firebase Web SDK runs auth network calls inside
 * the iOS WKWebView, which is well-documented to be fragile on iOS 26
 * — ITP storage eviction (7-day cap), `capacitor://localhost` origin
 * blocked at the Firebase Auth API, and signInWithPopup is structurally
 * broken (no parent window). Apple App Store review 8560ac5f
 * (June 2026) rejected the app for the demo account stalling on the
 * "PROCESSING…" button — this was the root cause.
 *
 * The Capawesome `@capacitor-firebase/authentication` plugin uses the
 * NATIVE Firebase iOS SDK (Swift) for the auth network call. The web
 * SDK becomes a thin sync layer that mirrors auth state after the
 * native call returns. This eliminates WKWebView from the auth network
 * path entirely.
 *
 * Flow on native:
 *   1. Call the Capacitor plugin's signInWithEmailAndPassword (native Swift).
 *   2. Plugin returns a Firebase ID token.
 *   3. We call signInWithCredential on the JS SDK to sync auth state,
 *      so Firestore queries that gate on `auth.currentUser` work.
 *
 * Flow on web:
 *   Pure JS SDK — no plugin involved. signInWithEmailAndPassword runs
 *   the existing browser flow with the 20s withTimeout wrapper.
 *
 * Email/password on iOS requires `skipNativeAuth: false` (the default) —
 * the plugin enforces this, so don't pass skipNativeAuth=true for it.
 *
 * For Google sign-in, the plugin also handles the native OAuth round-
 * trip (no broken signInWithPopup in WKWebView). signInWithCredential
 * follows the same dual-sign pattern.
 *
 * Browser-safe: lazily imports the Capacitor plugin only when running
 * on a native platform. The plugin's iOS native code never ships to
 * the web bundle.
 */

import type { User, UserCredential } from 'firebase/auth';
import { auth, functions } from './firebase';

/**
 * Bridge a proven native Firebase session into the web JS SDK using a
 * custom token, instead of re-running the email/password network flow.
 *
 * The web SDK's signInWithEmailAndPassword hangs in iOS WKWebView
 * (`capacitor://localhost` fails reCAPTCHA/origin checks; ITP evicts auth
 * storage) — that's the "signed in natively but JS SDK sync failed" bug.
 * We instead: pull the native ID token → ask our backend to mint a custom
 * token for that uid (createAuthBridgeToken) → signInWithCustomToken, which
 * is a plain token exchange that skips reCAPTCHA and origin checks.
 */
async function bridgeNativeSessionToJsSdk(expectedUid: string): Promise<User> {
  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
  const { token } = await FirebaseAuthentication.getIdToken();
  if (!token) throw new Error('Native session returned no ID token');

  const { httpsCallable } = await import('firebase/functions');
  const mint = httpsCallable<{ idToken: string }, { customToken: string }>(
    functions,
    'createAuthBridgeToken'
  );
  // Cap the callable so a slow/cold function can't hang the whole sign-in
  // (httpsCallable's own timeout is 70s — far past AuthContext's 20s budget).
  const mintResult = await Promise.race([
    mint({ idToken: token }),
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error('auth-bridge-timeout')), 10_000)),
  ]);
  const { data } = mintResult;
  if (!data?.customToken) throw new Error('Auth bridge returned no custom token');

  const { signInWithCustomToken } = await import('firebase/auth');
  const cred = await signInWithCustomToken(auth, data.customToken);
  if (cred.user.uid !== expectedUid) {
    console.warn('[nativeAuth] bridged uid mismatch:', cred.user.uid, '!=', expectedUid);
  }
  return cred.user;
}

/** Check whether we're running inside a Capacitor native shell.
 *  Cached after first call — Capacitor.isNativePlatform() reads the
 *  user-agent which doesn't change at runtime. */
let _isNative: boolean | null = null;
async function isNativePlatform(): Promise<boolean> {
  if (_isNative !== null) return _isNative;
  try {
    const { Capacitor } = await import('@capacitor/core');
    _isNative = Capacitor.isNativePlatform();
    return _isNative;
  } catch {
    _isNative = false;
    return false;
  }
}

/**
 * Sign in with email + password. Routes through native Firebase iOS
 * SDK on native platforms; web SDK on browser. Returns the synced
 * Firebase JS SDK user so the rest of the app (Firestore, etc.) works
 * unchanged.
 */
export async function nativeAwareSignIn(email: string, password: string): Promise<User> {
  const native = await isNativePlatform();
  if (!native) {
    // Browser path — use the JS SDK directly with the existing 20s
    // timeout wrapper (lives in AuthContext.signIn).
    const { signInWithEmailAndPassword } = await import('firebase/auth');
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
  }

  // Native path — DUAL SIGN per canonical Capawesome pattern for
  // email/password (firebase-js-sdk bridge guide):
  //
  //   1. Plugin signInWithEmailAndPassword — native Swift SDK,
  //      bypasses WKWebView entirely. This is the call that DOESN'T
  //      hang on iOS 26 ITP/storage issues.
  //   2. Web SDK signInWithEmailAndPassword with SAME credentials —
  //      to sync JS SDK auth state for Firestore queries that gate
  //      on auth.currentUser.
  //
  // The previous "wait for onAuthStateChanged after native sign-in"
  // approach failed because the plugin and JS SDK don't share an
  // event bus by default — they're separate Firebase instances on
  // iOS. The web sign-in IS the sync mechanism. The native call has
  // already proven the credentials are valid + warmed the iOS auth
  // session, so the web call is fast (or at least, faster than a
  // cold web-only call which was the original problem).
  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
  // Fail-fast: bound the native call itself so a hung native SDK surfaces a
  // clear error in ~8s instead of riding the outer 13s budget to the wire.
  const result = await Promise.race([
    FirebaseAuthentication.signInWithEmailAndPassword({ email, password }),
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error('native-signin-timeout')), 8000)),
  ]);
  if (!result.user) {
    throw new Error('Native sign-in returned no user');
  }

  // PRIMARY: bridge the native session into the JS SDK via a custom token.
  // This avoids the WKWebView signInWithEmailAndPassword hang entirely.
  try {
    return await bridgeNativeSessionToJsSdk(result.user.uid);
  } catch (bridgeErr: any) {
    console.warn('[nativeAuth] custom-token bridge failed, falling back to web re-sign:', bridgeErr?.message);
  }

  // FALLBACK (legacy): re-run the web password flow. Kept so the app and the
  // createAuthBridgeToken function can deploy in either order — if the bridge
  // function isn't live yet, behaviour degrades to the previous path rather
  // than failing outright. Short timeout — if it stalls, native session is
  // still valid; downgrade gracefully.
  const { signInWithEmailAndPassword } = await import('firebase/auth');
  try {
    const cred = await Promise.race([
      signInWithEmailAndPassword(auth, email, password),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('web-sdk-sync-timeout')), 5000)
      ),
    ]);
    return cred.user;
  } catch (e: any) {
    console.warn('[nativeAuth] web SDK sync failed/stalled — native session is valid:', e?.message);
    // Fall back to auth.currentUser if onAuthStateChanged already
    // populated it. If not, the caller will see a partially-signed-in
    // state that resolves on next app launch via auth state restore.
    if (auth.currentUser && auth.currentUser.uid === result.user.uid) {
      return auth.currentUser;
    }
    // Native session valid but JS SDK didn't sync — throw a different
    // error so we can distinguish "credentials wrong" from "sync stall."
    throw new Error('Signed in natively but JS SDK sync failed — please try again');
  }
}

/**
 * Sign up — same pattern.
 */
export async function nativeAwareSignUp(email: string, password: string): Promise<User> {
  const native = await isNativePlatform();
  if (!native) {
    const { createUserWithEmailAndPassword } = await import('firebase/auth');
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    return cred.user;
  }

  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
  const result = await FirebaseAuthentication.createUserWithEmailAndPassword({ email, password });
  if (!result.user) {
    throw new Error('Native sign-up returned no user');
  }

  // Same bridge pattern as nativeAwareSignIn — adopt the freshly-created
  // native session into the JS SDK via a custom token, falling back to the
  // legacy web re-sign if the bridge function isn't available yet.
  try {
    return await bridgeNativeSessionToJsSdk(result.user.uid);
  } catch (bridgeErr: any) {
    console.warn('[nativeAuth] custom-token bridge failed after signup, falling back to web re-sign:', bridgeErr?.message);
  }

  const { signInWithEmailAndPassword } = await import('firebase/auth');
  try {
    const cred = await Promise.race([
      signInWithEmailAndPassword(auth, email, password),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('web-sdk-sync-timeout')), 5000)
      ),
    ]);
    return cred.user;
  } catch (e: any) {
    console.warn('[nativeAuth] web SDK sync failed after native signup:', e?.message);
    if (auth.currentUser && auth.currentUser.uid === result.user.uid) {
      return auth.currentUser;
    }
    throw new Error('Signed up natively but JS SDK sync failed — please try signing in');
  }
}

/**
 * Google sign-in. On native, uses the plugin's native Google OAuth
 * flow (avoids the broken signInWithPopup in WKWebView). On browser,
 * uses the JS SDK popup.
 */
export async function nativeAwareSignInWithGoogle(): Promise<User> {
  const native = await isNativePlatform();
  if (!native) {
    const { signInWithPopup, GoogleAuthProvider } = await import('firebase/auth');
    const provider = new GoogleAuthProvider();
    const cred = await signInWithPopup(auth, provider);
    return cred.user;
  }

  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
  const result = await FirebaseAuthentication.signInWithGoogle();
  if (!result.user || !result.credential?.idToken) {
    throw new Error('Native Google sign-in returned no credential');
  }
  // Bridge into the JS SDK with the ID token from the native plugin.
  const { signInWithCredential, GoogleAuthProvider } = await import('firebase/auth');
  const credential = GoogleAuthProvider.credential(result.credential.idToken);
  const cred = await signInWithCredential(auth, credential);
  return cred.user;
}

/**
 * Sign out from both layers — native plugin AND JS SDK — so neither
 * holds a stale session. Native-only sign-out can leave the JS SDK
 * thinking the user is still authenticated.
 */
export async function nativeAwareSignOut(): Promise<void> {
  const native = await isNativePlatform();
  const { signOut } = await import('firebase/auth');
  if (native) {
    try {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      await FirebaseAuthentication.signOut();
    } catch (err) {
      console.warn('[nativeAuth] plugin sign-out failed:', err);
    }
  }
  await signOut(auth);
}
